import { FC, useCallback, useEffect, useState } from 'react';
import styles from './CookieConsent.module.scss';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import { privacyPolicyPageRoute } from '@kronoss/utils/routes';
import Cookies from 'js-cookie';

const COOKIE_CONSENT_KEY = 'cookie_consent';

export interface CookiePreferences {
  necessary: boolean;
  marketing: boolean;
  personalization: boolean;
  analytics: boolean;
}

const defaultPreferences: CookiePreferences = {
  necessary: true,
  marketing: false,
  personalization: false,
  analytics: false,
};

const CookieConsent: FC = () => {
  const { t } = useTranslation('common');
  const [visible, setVisible] = useState(false);
  const [preferences, setPreferences] =
    useState<CookiePreferences>(defaultPreferences);

  const openWithSavedPreferences = useCallback(() => {
    const saved = Cookies.get(COOKIE_CONSENT_KEY);
    if (saved) {
      try {
        setPreferences(JSON.parse(saved));
      } catch {
        setPreferences(defaultPreferences);
      }
    }
    setVisible(true);
  }, []);

  useEffect(() => {
    const consent = Cookies.get(COOKIE_CONSENT_KEY);
    if (!consent) {
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    const handler = () => openWithSavedPreferences();
    window.addEventListener('open-cookie-settings', handler);
    return () => window.removeEventListener('open-cookie-settings', handler);
  }, [openWithSavedPreferences]);

  const handleToggle = (key: keyof CookiePreferences) => {
    if (key === 'necessary') return;
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const saveConsent = (prefs: CookiePreferences) => {
    Cookies.set(COOKIE_CONSENT_KEY, JSON.stringify(prefs), {
      expires: 365,
      path: '/',
      sameSite: 'lax',
    });
    setVisible(false);
  };

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      marketing: true,
      personalization: true,
      analytics: true,
    };
    setPreferences(allAccepted);
    saveConsent(allAccepted);
  };

  const handleSave = () => {
    saveConsent(preferences);
  };

  if (!visible) return null;

  const categories: {
    key: keyof CookiePreferences;
    labelKey: string;
    descKey: string;
    disabled: boolean;
  }[] = [
    {
      key: 'necessary',
      labelKey: 'cookiesConsent.cookies.necessary.label',
      descKey: 'cookiesConsent.cookies.necessary.description',
      disabled: true,
    },
    {
      key: 'analytics',
      labelKey: 'cookiesConsent.cookies.analytics.label',
      descKey: 'cookiesConsent.cookies.analytics.description',
      disabled: false,
    },
    {
      key: 'marketing',
      labelKey: 'cookiesConsent.cookies.marketing.label',
      descKey: 'cookiesConsent.cookies.marketing.description',
      disabled: false,
    },
    {
      key: 'personalization',
      labelKey: 'cookiesConsent.cookies.personalization.label',
      descKey: 'cookiesConsent.cookies.personalization.description',
      disabled: false,
    },
  ];

  return (
    <div className={styles.Overlay}>
      <div className={styles.Banner}>
        <div className={styles.Title}>{t('cookiesConsent.title')}</div>
        <div className={styles.Description}>
          {t('cookiesConsent.agreeText')}{' '}
          <Link
            href={privacyPolicyPageRoute}
            className={styles.PrivacyLink}
          >
            {t('cookiesConsent.privacyLearnMore')}
          </Link>
        </div>

        <div className={styles.Categories}>
          {categories.map((cat) => (
            <div key={cat.key} className={styles.Category}>
              <div className={styles.CategoryInfo}>
                <div className={styles.CategoryLabel}>{t(cat.labelKey)}</div>
                <div className={styles.CategoryDescription}>
                  {t(cat.descKey)}
                </div>
              </div>
              <label className={styles.Toggle}>
                <input
                  type="checkbox"
                  checked={preferences[cat.key]}
                  disabled={cat.disabled}
                  onChange={() => handleToggle(cat.key)}
                />
                <span className={styles.Slider} />
              </label>
            </div>
          ))}
        </div>

        <div className={styles.Actions}>
          <button className={styles.SaveButton} onClick={handleSave}>
            {t('cookiesConsent.save')}
          </button>
          <button className={styles.AcceptButton} onClick={handleAcceptAll}>
            {t('cookiesConsent.acceptCookies')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
